import { cn } from "@/lib/utils";
import { useId } from "react";

/**
 * The Counselor Home swoosh: a wave across the top of a page with a coral glow
 * from the top-left corner fading to lavender ("1b corner glow"). Turned
 * 180° for the bottom of a section, so the coral glows from the bottom-right.
 * Absolutely positioned, so put it first inside a `relative isolate`
 * container that spans the full width; a bottom swoosh's container also needs
 * `overflow-hidden`, since the layer is taller than most bottom sections.
 */
export const SwooshBackdrop = ({
  corner = "top-left",
  isPageHeight = false,
}: {
  /** Where the coral glows from, and so which edge the layer is anchored to. */
  corner?: "top-left" | "bottom-right";
  /** Stretch over the whole container, as on Counselor Home, instead of 1100px. */
  isPageHeight?: boolean;
}) => {
  const gradientId = useId();
  const isTopLeft = corner === "top-left";
  const path = (
    <path
      d="M0 350 C 240 430, 440 440, 600 345 C 760 250, 860 200, 1000 200 L1000 0 L0 0 Z"
      fill={`url(#${gradientId})`}
    />
  );
  return (
    <svg
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-x-0 -z-10 w-full",
        isTopLeft ? "top-0" : "bottom-0",
        // Its height sets how deep the curve dips and how far the coral
        // reaches, so it's fixed on desktop and scales with the width below.
        isPageHeight ? "h-full" : "h-[86vw] md:h-[1100px]",
      )}
      viewBox="0 0 1000 1000"
      preserveAspectRatio="none"
    >
      <defs>
        <radialGradient
          id={gradientId}
          cx="0"
          cy="0"
          r="0.8"
          gradientTransform="scale(1 2.2)"
        >
          <stop offset="0" stopColor="#fde4df" />
          <stop offset="0.12" stopColor="#fde4df" />
          <stop offset="0.5" stopColor="#f6ebf4" />
          <stop offset="1" stopColor="#e9e7fb" />
        </radialGradient>
      </defs>
      {isTopLeft ? path : <g transform="rotate(180 500 500)">{path}</g>}
    </svg>
  );
};

/**
 * The swoosh's gradient filling its parent, for a band that isn't wave-shaped:
 * coral in the top-left corner, or mirrored into the bottom-right for a
 * bottom band. Put it first inside a `relative isolate` container.
 */
export const SwooshGradientFill = ({
  corner,
}: {
  corner: "top-left" | "bottom-right";
}) => {
  const gradientId = useId();
  const isTopLeft = corner === "top-left";
  return (
    <svg
      aria-hidden
      className="pointer-events-none absolute inset-0 -z-10 h-full w-full"
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient
          id={gradientId}
          x1={isTopLeft ? 0 : 1}
          y1={isTopLeft ? 0 : 1}
          x2={isTopLeft ? 1 : 0}
          y2={isTopLeft ? 0.35 : 0.65}
        >
          <stop offset="0" stopColor="#fde4df" />
          <stop offset="0.24" stopColor="#f6ebf4" />
          <stop offset="1" stopColor="#e9e7fb" />
        </linearGradient>
      </defs>
      <rect width="1" height="1" fill={`url(#${gradientId})`} />
    </svg>
  );
};
